import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Scale, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { LEGAL_FORM_OPTIONS, NEEDS_REPRESENTATIVE, NEEDS_REGISTER } from "../constants/legalForms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  ["owner_name", "Vor- und Nachname (Inhaber/in)"],
  ["company_name", "Firmenname"],
  ["street", "Straße & Hausnummer"],
  ["zip_code", "PLZ"],
  ["city", "Ort"],
  ["notification_email", "E-Mail-Adresse (muss Ihnen gehören)"],
  ["phone", "Telefon (optional)"],
  ["vat_id", "USt-IdNr. (nur falls vorhanden)"],
];

export function LegalDataCard({ page, pageId, onSaved }) {
  const [form, setForm] = useState({});
  const [missing, setMissing] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const init = useCallback(() => {
    const f = {};
    [...FIELDS.map(x => x[0]), "legal_form", "represented_by", "register_court", "register_number", "profession_info"]
      .forEach(k => { f[k] = page?.[k] || ""; });
    setForm(f);
  }, [page]);

  useEffect(() => { init(); }, [init]);
  useEffect(() => {
    axios.get(`${API}/pages/${pageId}/legal`).then(r => setMissing(r.data.missing_fields || [])).catch(() => {});
  }, [pageId, page]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const required = missing.filter(m => m.severity === "required");
  const optional = missing.filter(m => m.severity !== "required");

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.put(`${API}/pages/${pageId}/legal-data`, form, { headers: { Authorization: `Bearer ${token}` } });
      setMissing(res.data.missing_fields || []);
      toast.success("Rechtliche Angaben gespeichert");
      setOpen(false);
      onSaved && onSaved(form);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const showRep = NEEDS_REPRESENTATIVE.includes(form.legal_form);
  const showReg = NEEDS_REGISTER.includes(form.legal_form);

  return (
    <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-100 mb-6" data-testid="legal-data-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Scale className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-bold text-slate-900">Impressum & Datenschutz</h2>
            <p className="text-sm text-slate-500 max-w-xl">Die Rechtstexte Ihrer Seite werden automatisch aus Ihren Angaben erzeugt. Platzhalter werden nie übernommen.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen(o => !o)} data-testid="legal-data-edit-btn" className="rounded-full">
          {open ? "Schließen" : "Angaben bearbeiten"}
        </Button>
      </div>

      {required.length > 0 ? (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="legal-missing-list">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" />Noch fehlende Pflichtangaben</p>
          <ul className="text-sm text-amber-800 space-y-1 list-disc pl-5">
            {required.map(m => <li key={m.field}>{m.label}{m.hint ? <span className="text-amber-700/80"> – {m.hint}</span> : null}</li>)}
          </ul>
        </div>
      ) : (
        <p className="mt-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="legal-complete"><CheckCircle2 className="w-4 h-4" />Alle Pflichtangaben für das Impressum sind vorhanden.</p>
      )}
      {optional.length > 0 && (
        <p className="mt-2 text-xs text-slate-500" data-testid="legal-optional-list">Optional: {optional.map(m => m.label).join(", ")}</p>
      )}

      {open && (
        <div className="mt-5 grid sm:grid-cols-2 gap-4" data-testid="legal-data-form">
          <div className="sm:col-span-2">
            <Label>Rechtsform</Label>
            <select value={form.legal_form} onChange={e => set("legal_form", e.target.value)} data-testid="legal-form-select"
              className="mt-1.5 w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">Bitte wählen …</option>
              {LEGAL_FORM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label>{label}</Label>
              <Input value={form[k] || ""} onChange={e => set(k, e.target.value)} className="mt-1.5 h-11" data-testid={`legal-field-${k}`} />
            </div>
          ))}
          {showRep && (
            <div className="sm:col-span-2">
              <Label>{form.legal_form === "gbr" ? "Gesellschafter / Vertretungsberechtigte" : "Vertretungsberechtigte (z. B. Geschäftsführer/in)"}</Label>
              <Input value={form.represented_by} onChange={e => set("represented_by", e.target.value)} className="mt-1.5 h-11" data-testid="legal-field-represented_by" />
            </div>
          )}
          {showReg && (
            <>
              <div><Label>Registergericht</Label><Input value={form.register_court} onChange={e => set("register_court", e.target.value)} className="mt-1.5 h-11" data-testid="legal-field-register_court" /></div>
              <div><Label>Registernummer</Label><Input value={form.register_number} onChange={e => set("register_number", e.target.value)} className="mt-1.5 h-11" data-testid="legal-field-register_number" /></div>
            </>
          )}
          <div className="sm:col-span-2">
            <Label>Berufsrechtliche Angaben (nur bei reglementierten Berufen)</Label>
            <Input value={form.profession_info} onChange={e => set("profession_info", e.target.value)} className="mt-1.5 h-11" data-testid="legal-field-profession_info"
              placeholder="Berufsbezeichnung, zuständige Kammer/Aufsichtsbehörde, berufsrechtliche Regelungen" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button onClick={save} disabled={saving} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="legal-data-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
            </Button>
          </div>
          <p className="sm:col-span-2 text-xs text-slate-400">Technische und inhaltliche Optimierung – ersetzt keine individuelle Rechtsberatung.</p>
        </div>
      )}
    </div>
  );
}
